import React, { useEffect, useState } from "react";
import ButtonPrimary from "../components/buttons/ButtonPrimary";
import ButtonSecondary from "../components/buttons/ButtonSecondary";
import CheckboxLabel from "../components/buttons/CheckboxLabel";
import ButtonDelete from "../components/buttons/ButtonDelete";
import { toast, ToastContainer } from "react-toastify";
import ModalConfirmSubscriptions from "../components/modal/modalConfirmSubscriptions";
import ModalAddCard from "../components/modal/modalAddCard";
import ModalDeleteCard from "../components/modal/modalDeleteCard";
import { Hourglass } from "react-loader-spinner";
import useBillingApi from "../hooks/api/useBillingApi";

const BillingInfo = () => {
  const [isOpenModalSubscription, setIsOpenModalSubscription] = useState(false);
  const [isOpenModalAddCard, setIsOpenModalAddCard] = useState(false);
  const [isOpenModalDeleteCard, setIsOpenModalDeleteCard] = useState(false);
  const [selectedCardForDelete, setSelectedCardForDelete] = useState(null);
  const [isSubscriptionCanceled, setIsSubscriptionCanceled] = useState(false);
  const [renewalDate, setRenewalDate] = useState(null);
  const [invoicesData, setInvoicesData] = useState([]);
  const [cardsData, setCardsData] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);

  const [loading, setLoading] = useState(false);
  const { fetchInvoices, subscriptionAction, fetchCards, setDefaultCard } = useBillingApi();

  const handleOpenModal = () => {
    setIsOpenModalSubscription(true);
  };

  const closeModal = () => {
    setIsOpenModalSubscription(false);
  };

  const handleOpenAddCardModal = () => {
    setIsOpenModalAddCard(true);
  };

  const handleCloseAddCardModal = () => {
    setIsOpenModalAddCard(false);
  };

  const handleOpenDeleteCardModal = (card) => {
    setSelectedCardForDelete(card);
    setIsOpenModalDeleteCard(true);
  };

  const handleCloseDeleteCardModal = () => {
    setIsOpenModalDeleteCard(false);
    setSelectedCardForDelete(null);
  };

  const handleCardAdded = async () => {
    // Reload cards after adding a new one
    setLoadingCards(true);
    try {
      const response = await fetchCards();
      const { cards } = response.data;
      if (cards && cards.length > 0) {
        setCardsData(cards);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || "Error fetching cards";
      toast.error(errorMessage);
    } finally {
      setLoadingCards(false);
    }
  };

  const handleSetDefaultCard = async (cardId, newCheckedValue) => {
    // If user is unchecking (setting to false), prevent it if it's the only card or show a message
    if (!newCheckedValue) {
      const currentCard = cardsData.find((card) => card.id === cardId);
      if (currentCard?.is_default && cardsData.length === 1) {
        toast.error("You must have at least one default card");
        return;
      }
      // Allow unchecking if there are other cards, but don't make API call
      // The backend should handle this, but for now we'll just prevent unchecking
      toast.info("Please set another card as default first");
      return;
    }

    try {
      const response = await setDefaultCard(cardId);
      if (response.status === 200) {
        toast.success(response.data.message || "Default card updated successfully");
        // Reload cards to reflect the change
        await handleCardAdded();
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || "Error setting default card";
      toast.error(errorMessage);
    }
  };

  ///// GET DATA FROM BILLING PAGE
  useEffect(() => {
    if (!isSubscriptionCanceled) {
      setLoading(true);
      fetchInvoices()
        .then((responseSettings) => {
          const data = responseSettings.data;

          if (data) {
            const renewalDate = data.renewalDate;
            const isCanceled = data.isCanceled;
            if (isCanceled === false) {
              setRenewalDate(renewalDate);
            } else {
              setIsSubscriptionCanceled(true);
            }
          }
          const { invoices } = responseSettings.data;

          if (invoices && invoices.length > 0) {
            setInvoicesData(invoices);
          } else {
            toast.error("No invoices found.");
          }

          setLoading(false);
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error?.message || "Error fetching data";
          toast.error(errorMessage);
          setLoading(false);
        });
    }
  }, [isSubscriptionCanceled, fetchInvoices]);

  ///// GET CARDS DATA
  useEffect(() => {
    setLoadingCards(true);
    fetchCards()
      .then((response) => {
        const { cards } = response.data;
        if (cards && cards.length > 0) {
          setCardsData(cards);
        }
        setLoadingCards(false);
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.error || error?.message || "Error fetching cards";
        toast.error(errorMessage);
        setLoadingCards(false);
      });
  }, [fetchCards]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleRenewal = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split("T")[0];

    const newData = {
      isCanceled: false,
      renewalDate: formattedDate,
    };
    setLoading(true);
    subscriptionAction(newData)
      .then((response) => {
        toast.success("Renewed with successfully!", response.data);
        setLoading(false);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      })
      .catch((error) => {
        toast.error("Error renewing subscription:", error);
      });
  };
  const formatCurrency = (amount) => {
    const dollars = Math.floor(amount / 100);
    return `${dollars} $`;
  };

  const getCardBrandName = (brand) => {
    const brandMap = {
      visa: "VISA",
      mastercard: "MASTERCARD",
      amex: "AMEX",
      discover: "DISCOVER",
    };
    return brandMap[brand?.toLowerCase()] || brand?.toUpperCase() || "CARD";
  };

  const getCardBrandIcon = (brand) => {
    const brandMap = {
      visa: "visa.svg",
      mastercard: "mastercard.svg",
      amex: "amex.svg",
      discover: "discover.svg",
    };
    return brandMap[brand?.toLowerCase()] || "visa.svg";
  };

  const formatExpirationDate = (month, year) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthName = monthNames[month - 1] || monthNames[0];
    return `${monthName} ${year}`;
  };
  return (
    <>
      <div className="content flex flex-col desktop:h-full mobile:p-6 bg-gray2">
        <div className="space-y-9">
          <h6 className="text-h6 text-dark4 ">Billing info</h6>
          {invoicesData.map((invoice) => (
            <div key={invoice.id} className="mb-4 space-y-2">
              <p className="text-bodyMR text-black">
                Subscription created:{" "}
                <span className="text-bodyLSB text-primary">
                  {formatDate(new Date(invoice.created * 1000))}
                </span>
              </p>
              <p className="text-bodyMR text-black">
                Amount:{" "}
                <span className="text-bodyLSB text-primary">
                  {formatCurrency(invoice.amount_paid)}
                </span>
              </p>
              <a
                href={invoice.invoice_pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-bodyMM cursor-pointer "
              >
                Download invoice
              </a>
            </div>
          ))}
          {loading && (
            <div className="flex flex-row justify-center items-center mt-10">
              <Hourglass
                visible={true}
                height="40"
                width="40"
                animationDuration="2"
                ariaLabel="hourglass-loading"
                wrapperStyle={{}}
                wrapperClass=""
                colors={["#FF5400", "#FF5400"]}
              />
            </div>
          )}
          {!loading && (
            <div className="subscription bg-blueLight5 rounded cursor-pointer mobXS:w-full tabMD:w-1/2 lgDesktop:w-3/5">
              <div className="px-6 py-8 desktop:flex-row desktop:flex gap-20">
                <div className="">
                  <p
                    className={`text-bodyMR text-black ${isSubscriptionCanceled ? "hidden" : ""}`}
                  >
                    Subscription available until
                  </p>
                  <p className="text-bodyLSB text-primary mb-6">
                    {isSubscriptionCanceled
                      ? "You canceled the subscription."
                      : formatDate(renewalDate)}
                  </p>
                </div>
                {!isSubscriptionCanceled ? (
                  <>
                    <ButtonSecondary
                      text="Cancel subscription"
                      onClick={handleOpenModal}
                      customClass="mobile:hidden text-primary text-bodyMM ml-auto mt-4"
                    />
                    <div className="desktop:hidden">
                      <ButtonPrimary
                        text="Cancel subscription"
                        padding="px-[8px] py-[10px]"
                        onClick={handleOpenModal}
                      />
                    </div>
                  </>
                ) : (
                  <ButtonSecondary
                    text="Renewal"
                    customClass="text-primary text-bodyMM ml-auto mt-4"
                    onClick={handleRenewal}
                  />
                )}
              </div>
            </div>
          )}
          <div className="my-cards space-y-5 cursor-pointer">
            <h6 className="text-h6 text-dark4">My Cards</h6>

            {loadingCards ? (
              <div className="flex flex-row justify-center items-center mt-10">
                <Hourglass
                  visible={true}
                  height="40"
                  width="40"
                  animationDuration="2"
                  ariaLabel="hourglass-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                  colors={["#FF5400", "#FF5400"]}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {cardsData.map((card) => (
                  <div key={card.id} className="card-row bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    {/* Card Header */}
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src={`${process.env.PUBLIC_URL}/${getCardBrandIcon(card.brand)}`}
                        alt={card.brand}
                        className="w-12 h-8 object-contain"
                      />
                      <div className="flex flex-col flex-1">
                        <p className="text-bodyMR text-dark4 font-medium">
                          {getCardBrandName(card.brand)} **** {card.last4}
                        </p>
                        <p className="text-bodyESR text-dark6">
                          Expires at: {formatExpirationDate(card.exp_month, card.exp_year)}
                        </p>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="border-t border-gray-200 pt-4 mt-4 flex items-center justify-between">
                      <div className="flex flex-col">
                        <p className="text-bodyMM text-black">Main card</p>
                        <p className="text-bodyESR text-dark6">
                          Choose this card to be your main card
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div onClick={(e) => e.stopPropagation()}>
                          <CheckboxLabel
                            label=""
                            checked={card.is_default}
                            onChange={(checked) => handleSetDefaultCard(card.id, checked)}
                          />
                        </div>
                        <ButtonDelete
                          className="border-0"
                          deleteAction={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteCardModal(card);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add New Card */}
                <div
                  className="card-new bg-white rounded-lg border border-gray-200 border-dashed flex-col flex px-[15px] py-[22px] cursor-pointer hover:border-primary transition-colors"
                  onClick={handleOpenAddCardModal}
                >
                  <div className="flex-row flex justify-center gap-2">
                    <img src={`${process.env.PUBLIC_URL}/plus.svg`} alt="" className="w-[20px]" />
                    <button type="button" className="text-bodyMM text-primary">
                      Add new card
                    </button>
                  </div>
                  <p className="text-bodyESR text-dark6 text-center py-2">
                    Click to add a new payment card
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="buttons-action mt-auto mobile:px-6 hidden">
          <div className="flex-row flex gap-2 cursor-pointer mobXS:justify-center mobile:pt-8 desktop:mb-8">
            <ButtonSecondary
              text="Update"
              customClass="bg-primary text-white text-center justify-center"
              width="mobXS:w-full tabMD:w-1/4 desktop:w-[150px]"
              padding="px-[5px] py-[10px]"
            />
            <ButtonSecondary
              text="Discard changes"
              customClass="bg-white border text-dark4 text-center justify-center"
              width="mobXS:w-full tabMD:w-1/4 desktop:w-[150px]"
              padding="px-[5px] py-[10px]"
              onClick={handleOpenModal}
            />
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={7000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {isOpenModalSubscription && (
        <ModalConfirmSubscriptions
          titleModal="Are you sure you want to delete the subscription?"
          renewalDate={renewalDate}
          isSubscriptionCanceled={isSubscriptionCanceled}
          setRenewalDate={setRenewalDate}
          setIsSubscriptionCanceled={setIsSubscriptionCanceled}
          closeModal={closeModal}
        />
      )}
      {isOpenModalAddCard && (
        <ModalAddCard
          isOpen={isOpenModalAddCard}
          onClose={handleCloseAddCardModal}
          onCardAdded={handleCardAdded}
        />
      )}
      {isOpenModalDeleteCard && (
        <ModalDeleteCard
          isOpen={isOpenModalDeleteCard}
          onClose={handleCloseDeleteCardModal}
          card={selectedCardForDelete}
          onCardDeleted={handleCardAdded}
        />
      )}
    </>
  );
};

export default BillingInfo;
